import { Link, useNavigate } from '@tanstack/react-router';
import {
  Bell,
  Building,
  ChevronsUpDown,
  CircleHelp,
  LogOut,
  MessageCircle,
  ShieldCheck,
  UserRound,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { Avatar, AvatarFallback } from '@nucleus-commerce/ui/components/avatar';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@nucleus-commerce/ui/components/sidebar';

import { session } from '@/shared/session/session';

function initials(name: string) {
  return name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/** Current user + sign out. `compact` renders a plain button for pages without the shell. */
export function UserMenu({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation('auth');
  const { me } = session.useSession();
  const logout = session.useLogout();
  const navigate = useNavigate();

  const signOut = () => {
    logout.mutate(undefined, { onSettled: () => void navigate({ to: '/login', replace: true }) });
  };

  if (!me) return null;

  if (compact) {
    return (
      <Button variant="link" size="sm" onClick={signOut} disabled={logout.isPending}>
        <LogOut aria-hidden />
        {t('userMenu.signOut')}
      </Button>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent"
              data-tour="shell.user-menu"
            >
              <Avatar className="size-8 rounded-lg">
                <AvatarFallback className="rounded-lg">{initials(me.user.name)}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="flex items-center gap-1.5">
                  <span className="truncate font-medium">{me.user.name}</span>
                  {me.twoFactorEnabled ? (
                    <ShieldCheck
                      className="text-primary size-3.5 shrink-0"
                      aria-label={t('userMenu.twoFactorOn')}
                      role="img"
                    />
                  ) : null}
                </span>
                <span className="text-muted-foreground truncate text-xs">{me.user.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" aria-hidden />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-56">
            <DropdownMenuLabel className="truncate">{me.organization.name}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/account">
                <UserRound aria-hidden />
                {t('userMenu.accountSettings')}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/account/security">
                <ShieldCheck aria-hidden />
                {t('userMenu.security')}
                {me.twoFactorEnabled ? (
                  <Badge variant="secondary" className="ml-auto" data-testid="two-factor-badge">
                    {t('userMenu.twoFactorBadge')}
                  </Badge>
                ) : null}
              </Link>
            </DropdownMenuItem>
            {me.twoFactorRequired ? null : (
              <Can permission="conversations.read">
                <DropdownMenuItem asChild>
                  <Link to="/account/chat-profile">
                    <MessageCircle aria-hidden />
                    {t('userMenu.chatProfile')}
                  </Link>
                </DropdownMenuItem>
              </Can>
            )}
            {me.twoFactorRequired ? null : (
              <DropdownMenuItem asChild>
                <Link to="/account/notifications">
                  <Bell aria-hidden />
                  {t('userMenu.notifications')}
                </Link>
              </DropdownMenuItem>
            )}
            {me.twoFactorRequired ? null : (
              <DropdownMenuItem asChild>
                <Link to="/help">
                  <CircleHelp aria-hidden />
                  {t('userMenu.help')}
                </Link>
              </DropdownMenuItem>
            )}
            {me.twoFactorRequired ? null : (
              <Can permission="settings.read">
                <DropdownMenuItem asChild>
                  <Link to="/org/settings">
                    <Building aria-hidden />
                    {t('userMenu.organizationSettings')}
                  </Link>
                </DropdownMenuItem>
              </Can>
            )}
            <DropdownMenuItem onSelect={signOut} disabled={logout.isPending}>
              <LogOut aria-hidden />
              {t('userMenu.signOut')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
